import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Stack, Typography } from '@mui/material';

import { downloadMaterialMutation } from 'api/material/query';
import { InputField } from 'components/v2/input/InputField';
import { InputSelectField } from 'components/v2/input/InputSelectField';
import { SwitchField } from 'components/v2/switch/SwitchField';
import { useLocalization } from 'localization';
import { useAuth } from 'modules/auth/hooks';
import { useVisualMaterials } from 'modules/material/hooks';
import { downloadMaterialSchema } from 'components/v2/division/form/schema/materialSchema';
import { MODAL_ID, MODALS_STORE } from 'constants/ui/modal';
import Modal from 'components/v2/modal/Modal';

const MaterialDownloadModal = () => {
  const { t } = useLocalization();
  const { user } = useAuth();
  const { toggleModal, isModalOpen, getModalData } = MODALS_STORE.cases(state => state);
  const { caseId, selectedMaterialIds } = getModalData(MODAL_ID.CASES_MATERIALS_DOWNLOAD);

  const { visualMaterials: materials } = useVisualMaterials(caseId);

  const defaultValues = {
    filter: 'all',
    email: user?.email ?? '',
    highQuality: true,
  };

  const { control, handleSubmit, watch } = useForm({
    resolver: yupResolver(downloadMaterialSchema()),
    defaultValues: defaultValues,
    values: defaultValues,
    mode: 'onChange',
  });

  const filter = watch('filter');
  const filteredMaterials = (() => {
    switch (filter) {
      case 'active':
        return materials.filter(({ isActive }) => isActive);
      case 'selected':
        return materials.filter(({ id }) => selectedMaterialIds?.includes(id));
      default:
        return materials;
    }
  })();

  const { mutateAsync, isLoading } = downloadMaterialMutation({
    caseId: caseId,
    email: watch('email'),
    webResolution: !watch('highQuality'), // web resolution means low quality
    materialIds: filteredMaterials.map(({ id }) => id?.toString()),
    handleSuccess: () => toggleModal(MODAL_ID.CASES_MATERIALS_DOWNLOAD),
  });

  const onSubmit = () => {
    mutateAsync();
  };

  const filteredOptions = [
    { label: 'common.terms.all', value: 'all' },
    { label: 'common.terms.active', value: 'active' },
  ];

  if (selectedMaterialIds && !!selectedMaterialIds?.length) {
    filteredOptions.push({ label: 'common.terms.selected', value: 'selected' });
  }

  return (
    <Modal
      modalTitle={t('modals.downloadMaterial.title')}
      onConfirm={handleSubmit(onSubmit)}
      isLoading={isLoading}
      confirmBtnLabel={t('common.actions.download')}
      isOpen={isModalOpen(MODAL_ID.CASES_MATERIALS_DOWNLOAD)}
      toggleModal={() => toggleModal(MODAL_ID.CASES_MATERIALS_DOWNLOAD)}
    >
      <Stack spacing={2}>
        <Typography variant="body1">{t('modals.downloadMaterial.instructions')}</Typography>
        <InputField control={control} name="email" label="common.terms.recipient" required />
        <InputSelectField
          name="filter"
          control={control}
          options={filteredOptions}
          label="common.terms.filter"
          menuHeight={300}
          required
        />
        <SwitchField label="common.terms.highQuality" control={control} name="highQuality" />
      </Stack>
    </Modal>
  );
};

export default MaterialDownloadModal;
//Old version: src\modules\material\MaterialDownloadModal\index.tsx
